import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BulkActionBar from "../../components/BulkActionBar";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
  requestBulkEmsApproval,
} from "../../lib/emsApproval";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/GuidedRegisters.css";
import {
  createAuditRegisterItemApi,
  decideEmsApprovalApi,
  deleteAuditRegisterItemApi,
  downloadAuditsRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchAuditsRegisterApi,
  requestEmsApprovalApi,
  updateAuditRegisterItemApi,
  verifyEmsClosureApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "internal_audits";

const EMPTY_FORM = {
  iso_clause: "",
  title: "",
  scope: "",
  auditor: "",
  audit_date: "",
  status: "planned",
  findings_summary: "",
  actions_required: "",
  next_review_date: "",
  evidence: "",
  notes: "",
};

export default function InternalAudits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAuditor, setFilterAuditor] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const [isoClauseLabels, setIsoClauseLabels] = useState({});
  const [defaultIsoClause, setDefaultIsoClause] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [verifyRoles, setVerifyRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [closureVerificationRequired, setClosureVerificationRequired] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRequesting, setBulkRequesting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchAuditsRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load audits register");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const policyRows = Array.isArray(payload?.policy) ? payload.policy : [];
      const labels = payload?.iso_clause_labels && typeof payload.iso_clause_labels === "object"
        ? payload.iso_clause_labels
        : {};
      const modulePolicy = policyRows.find((r) => String(r?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options)
        ? modulePolicy.iso_clause_options.map((x) => String(x))
        : [];
      const fallbackDefault = String(modulePolicy?.iso_clause_default || options[0] || "");
      const reqRoles = Array.isArray(modulePolicy?.request_roles) ? modulePolicy.request_roles.map((x) => String(x).toLowerCase()) : [];
      const appRoles = Array.isArray(modulePolicy?.approve_roles) ? modulePolicy.approve_roles.map((x) => String(x).toLowerCase()) : [];
      const revRoles = Array.isArray(modulePolicy?.review_roles) ? modulePolicy.review_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const verRoles = Array.isArray(modulePolicy?.verify_roles) ? modulePolicy.verify_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const wfMode = String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase();
      setIsoClauseLabels(labels);
      setDefaultIsoClause(fallbackDefault);
      setRequestRoles(reqRoles);
      setApproveRoles(appRoles);
      setReviewRoles(revRoles);
      setVerifyRoles(verRoles);
      setWorkflowMode(wfMode === "two_step" ? "two_step" : "one_step");
      setClosureVerificationRequired(Boolean(modulePolicy?.closure_verification_required));
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallbackDefault }));
    } catch {
      setIsoClauseLabels({});
      setDefaultIsoClause("9");
      setRequestRoles(["client", "admin"]);
      setApproveRoles(["admin"]);
      setReviewRoles(["admin"]);
      setVerifyRoles(["admin"]);
      setWorkflowMode("one_step");
      setClosureVerificationRequired(false);
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "9" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
    setSubmitAttempted(false);
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!String(form.title || "").trim()) errs.push("Audit title is required.");
    if (!String(form.audit_date || "").trim()) errs.push("Audit date is required.");
    if (!String(form.iso_clause || "").trim()) errs.push("ISO clause is required.");
    if (String(form.status || "").trim().toLowerCase() === "completed") {
      if (!String(form.scope || "").trim()) errs.push("Scope is required before setting status to completed.");
      if (!String(form.auditor || "").trim()) errs.push("Auditor is required before setting status to completed.");
      if (!String(form.findings_summary || "").trim()) errs.push("Findings summary is required before setting status to completed.");
      if (!String(form.actions_required || "").trim()) errs.push("Actions required is required before setting status to completed.");
      if (!String(form.evidence || "").trim()) errs.push("Evidence is required before setting status to completed.");
    }
    return errs;
  }, [form]);

  const statusBadgeClass = (status) => `badge badge-status-${String(status || "").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const approvalBadgeClass = (status) => `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const userRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(userRole);
  const canApprove = approveRoles.includes(userRole) && canApproveEmsModule("internal_audits");
  const canReview = reviewRoles.includes(userRole);
  const canVerify = verifyRoles.includes(userRole);
  const completionStatuses = useMemo(() => ["completed"], []);
  const canRequestApprovalForItem = useCallback((item) =>
    canRequestApprovalForRecord(item, canRequest, completionStatuses),
    [canRequest, completionStatuses]
  );
  const isCompletionStatus = (status) => completionStatuses.includes(String(status || "").trim().toLowerCase());

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      title: item.title || "",
      scope: item.scope || "",
      auditor: item.auditor || "",
      audit_date: item.audit_date || "",
      status: item.status || "planned",
      findings_summary: item.findings_summary || "",
      actions_required: item.actions_required || "",
      next_review_date: item.next_review_date || "",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await updateAuditRegisterItemApi(editingId, form);
        toast.success("Audit updated");
      } else {
        await createAuditRegisterItemApi(form);
        toast.success("Audit created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save audit");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this internal audit entry?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteAuditRegisterItemApi(id);
      toast.success("Audit deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete audit");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        decision,
        notes,
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision}`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional closure verification note:", "") || "";
      await verifyEmsClosureApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadAuditsRegisterCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audits_register.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Audits register exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export audits register");
    } finally {
      setExporting(false);
    }
  };

  const auditorOptions = useMemo(() => {
    const set = new Set(
      (items || [])
        .map((it) => String(it.auditor || "").trim())
        .filter(Boolean)
    );
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ym = new Date().toISOString().slice(0, 7);
    return (items || []).filter((it) => {
      if (filterStatus !== "all" && String(it.status || "") !== filterStatus) return false;
      if (filterAuditor === "__unassigned__") {
        if (String(it.auditor || "").trim()) return false;
      } else if (filterAuditor !== "all" && String(it.auditor || "") !== filterAuditor) {
        return false;
      }
      if (overdueOnly) {
        const rd = String(it.next_review_date || "").trim();
        if (!rd || rd >= today) return false;
      }
      if (dueThisMonthOnly) {
        const rd = String(it.next_review_date || "").trim();
        if (!rd.startsWith(ym)) return false;
      }
      return true;
    });
  }, [items, filterStatus, filterAuditor, overdueOnly, dueThisMonthOnly]);

  const selectableFilteredIds = useMemo(
    () => filteredItems.filter((it) => canRequestApprovalForItem(it)).map((it) => String(it.id)),
    [filteredItems, canRequestApprovalForItem]
  );
  const selectedSet = useMemo(() => new Set(selectedIds.map((x) => String(x))), [selectedIds]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => selectedSet.has(String(id)));
  const selectedEligibleItems = useMemo(
    () => filteredItems.filter((it) => selectedSet.has(String(it.id)) && canRequestApprovalForItem(it)),
    [filteredItems, selectedSet, canRequestApprovalForItem]
  );

  useEffect(() => {
    const liveIds = new Set((items || []).map((it) => String(it.id)));
    setSelectedIds((prev) => prev.filter((id) => liveIds.has(String(id))));
  }, [items]);

  const applyPreset = (preset) => {
    if (preset === "planned_only") {
      setFilterStatus("planned");
      setFilterAuditor("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterStatus("all");
      setFilterAuditor("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    if (preset === "unassigned_auditor") {
      setFilterStatus("all");
      setFilterAuditor("__unassigned__");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    setFilterStatus("all");
    setFilterAuditor("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const toggleSelectOne = (itemId, checked) => {
    const id = String(itemId);
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      for (const id of selectableFilteredIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return Array.from(next);
    });
  };

  const handleBulkRequestApproval = async () => {
    if (!selectedEligibleItems.length) {
      toast.error("Select at least one eligible row");
      return;
    }
    const notes = window.prompt("Optional bulk approval request note:", "") || "";
    try {
      setBulkRequesting(true);
      const { ok, failed } = await requestBulkEmsApproval(
        requestEmsApprovalApi,
        MODULE_KEY,
        selectedEligibleItems,
        notes
      );
      if (ok > 0) toast.success(`Requested approval for ${ok} item(s)`);
      if (failed > 0) toast.error(`${failed} item(s) failed`);
      setSelectedIds([]);
      await loadItems();
    } finally {
      setBulkRequesting(false);
    }
  };

  return (
    <div className="dashboard-page guided-register-page">
      <div className="dashboard-header">
        <div>
          <p className="guided-register-eyebrow">ISO 14001 performance evaluation</p>
          <h1>Internal Audits Register</h1>
          <p className="subtitle">Plan independent checks of your EMS, record findings, and retain evidence that actions have been followed through.</p>
        </div>
      </div>

      <section className="card guided-register-guidance">
        <div>
          <p className="guided-register-eyebrow">How to plan an audit</p>
          <h3>Define the audit, carry it out, then follow up its findings</h3>
          <p>Create one record for each audit. The completed record should show what was audited, who carried it out, what was found and how outstanding actions are managed.</p>
        </div>
        <div className="guided-register-guidance-grid">
          <div><strong>Plan</strong><span>Set the audit title, scope, independent auditor and audit date before the review begins.</span></div>
          <div><strong>Record findings</strong><span>Summarise conformities, non-conformities and opportunities for improvement clearly.</span></div>
          <div><strong>Close the loop</strong><span>Assign follow-up actions, set a review date and attach the audit report and supporting records.</span></div>
        </div>
      </section>
      <div className="card settings-card guided-register-form">
        <div className="settings-header"><div><p className="guided-register-eyebrow">Your audit record</p><h3>{editingId ? "Edit Audit" : "Add Audit"}</h3></div><span className="kpi-sub">Plan the review first; complete the findings, actions and evidence before marking it complete.</span></div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} labels={isoClauseLabels} />
            <label className="settings-field">
              <span>Audit title</span>
              <input placeholder="e.g. Annual EMS internal audit" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving} />
              <small>Use a clear audit name that identifies the period, site or focus.</small>
            </label>
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving}>
                <option value="planned">planned</option>
                <option value="in_progress">in_progress</option>
                <option value="completed">completed</option>
                <option value="overdue">overdue</option>
              </select>
              <small>Use planned before the audit, in progress while it is underway, and completed only when findings and evidence are recorded.</small>
            </label>
            <label className="settings-field">
              <span>Scope</span>
              <input placeholder="e.g. Waste, energy and legal compliance processes" value={form.scope} onChange={(e) => setField("scope", e.target.value)} disabled={saving} />
              <small>State the sites, processes, clauses or activities included in this audit.</small>
            </label>
            <label className="settings-field">
              <span>Auditor</span>
              <input placeholder="e.g. Alex Taylor" value={form.auditor} onChange={(e) => setField("auditor", e.target.value)} disabled={saving} />
              <small>Name a suitably independent auditor where practical.</small>
            </label>
            <label className="settings-field">
              <span>Audit date</span>
              <input type="date" value={form.audit_date} onChange={(e) => setField("audit_date", e.target.value)} disabled={saving} />
              <small>Set the planned or actual date on which the audit is performed.</small>
            </label>
            <label className="settings-field">
              <span>Next review date</span>
              <input type="date" value={form.next_review_date} onChange={(e) => setField("next_review_date", e.target.value)} disabled={saving} />
              <small>Set the next audit date or follow-up review deadline.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Findings summary</span>
              <input placeholder="e.g. One minor non-conformance: waste labels inconsistent in warehouse" value={form.findings_summary} onChange={(e) => setField("findings_summary", e.target.value)} disabled={saving} />
              <small>Summarise the key findings, including any non-conformities or improvements.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Actions required</span>
              <input placeholder="e.g. Update labels and complete a monthly inspection check" value={form.actions_required} onChange={(e) => setField("actions_required", e.target.value)} disabled={saving} />
              <small>Record the action needed, or say no action is required where appropriate.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence / link"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving}
              helpText="Link the audit plan, checklist, report, photographs, interview notes or completed action evidence."
            />
            <label className="settings-field guided-register-wide-field">
              <span>Notes</span>
              <input placeholder="Optional: record audit method, attendees, follow-up discussions or management decisions." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving} />
              <small>Use this space for context that will help when the audit is reviewed later.</small>
            </label>
          </div>
          {submitAttempted && validationErrors.length > 0 ? (
            <div className="form-error-summary">
              Please fix the following before saving:
              <ul>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="settings-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving} style={{ marginRight: 10 }}>
                Cancel
              </button>
            ) : null}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update audit" : "Add audit"}
            </button>
          </div>
        </form>
      </div>

      <BulkActionBar
        selectedCount={selectedIds.length}
        eligibleCount={selectedEligibleItems.length}
        onClear={() => setSelectedIds([])}
        onRequest={handleBulkRequestApproval}
        requesting={bulkRequesting}
        requestDisabled={bulkRequesting || selectedEligibleItems.length === 0}
        onExport={handleExportCsv}
        exporting={exporting}
      />

      <div className="filter-bar" style={{ gridTemplateColumns: "180px 180px auto 1fr" }}>
        <label className="settings-field">
          <span>Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">all</option>
            <option value="planned">planned</option>
            <option value="in_progress">in_progress</option>
            <option value="completed">completed</option>
            <option value="overdue">overdue</option>
          </select>
        </label>

        <label className="settings-field">
          <span>Auditor</span>
          <select value={filterAuditor} onChange={(e) => setFilterAuditor(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {auditorOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue review only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("planned_only")}>Only planned</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Review due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("unassigned_auditor")}>Unassigned auditor</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                  disabled={!selectableFilteredIds.length}
                />
              </th>
              <th>ISO Clause</th>
              <th>Title</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Auditor</th>
              <th>Audit date</th>
              <th>Next review</th>
              <th>Evidence</th>
              <th>Updated by</th>
              <th>Updated at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={12} className="table-empty">No audit entries yet.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(it.id))}
                      onChange={(e) => toggleSelectOne(it.id, e.target.checked)}
                      disabled={!canRequestApprovalForItem(it)}
                    />
                  </td>
                  <td>{it.iso_clause ? (isoClauseLabels[it.iso_clause] ? `${it.iso_clause} - ${isoClauseLabels[it.iso_clause]}` : `Clause ${it.iso_clause}`) : "-"}</td>
                  <td>{it.title || "-"}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status || "-"}</span></td>
                  <td><span className={approvalBadgeClass(it.approval_status)}>{it.approval_status || "not_requested"}</span></td>
                  <td>{it.auditor || "-"}</td>
                  <td>{it.audit_date || "-"}</td>
                  <td>{it.next_review_date || "-"}</td>
                  <td><EvidenceReferenceView value={it.evidence} /></td>
                  <td>{it.updated_by || "-"}</td>
                  <td>{it.updated_at || "-"}</td>
                  <td>
                    <button className="admin-btn secondary" onClick={() => startEdit(it)} style={{ marginRight: 8 }}>Edit</button>
                    {canRequestApprovalForItem(it) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleRequestApproval(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Request"}
                      </button>
                    ) : null}
                    {workflowMode === "two_step" && canReview && isApprovalPendingReview(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "review")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Review"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {canApprove && isApprovalPending(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "approve")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Approve"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {closureVerificationRequired
                    && canVerify
                    && isCompletionStatus(it.status)
                    && isApprovalApproved(it)
                    && !Boolean(it.closure_verified) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleVerifyClose(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Verify close"}
                      </button>
                    ) : null}
                    <button className="admin-btn danger" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id}>
                      {deletingId === it.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



